import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

export interface TenantOption {
  id: number;
  name: string;
}

interface TenantScopeContextType {
  tenants: TenantOption[];
  selectedTenantId: number | null; // null = "All tenants"
  setSelectedTenantId: (id: number | null) => void;
  selectedTenantName: string | null;
  // Ricarica l'elenco (es. dopo la creazione di un tenant dal wizard di onboarding): prima il
  // menu "Viewing" restava fermo fino al prossimo login.
  reloadTenants: () => Promise<void>;
}

const TenantScopeContext = createContext<TenantScopeContextType | null>(null);

const STORAGE_KEY = 'soc_selected_tenant';

// Gestisce il "tenant selezionato" per la console operativa: null significa vista aggregata
// su tutti i tenant accessibili all'utente, altrimenti filtra su un singolo cliente.
// La selezione è persistita in localStorage e sopravvive alla navigazione tra le pagine.
export function TenantScopeProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const reloadTenants = useCallback(async () => {
    const res = await axios.get('/api/tenants');
    setTenants(res.data.data);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get('/api/tenants').then(res => {
      setTenants(res.data.data);
      // Un utente di livello tenant vede solo il proprio: forziamo la selezione.
      if (user?.scopeLevel === 'tenant' && user.tenantId) {
        setSelectedTenantIdState(user.tenantId);
      }
    });
  }, [isAuthenticated, user]);

  const setSelectedTenantId = useCallback((id: number | null) => {
    setSelectedTenantIdState(id);
    if (id === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name ?? null;

  return (
    <TenantScopeContext.Provider value={{ tenants, selectedTenantId, setSelectedTenantId, selectedTenantName, reloadTenants }}>
      {children}
    </TenantScopeContext.Provider>
  );
}

export function useTenantScope(): TenantScopeContextType {
  const ctx = useContext(TenantScopeContext);
  if (!ctx) throw new Error('useTenantScope must be used within a TenantScopeProvider');
  return ctx;
}
